import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState } from 'react';
import PortedLightRoom from './PortedLightRoom';
import PortedHome from './PortedHome';
import PortedDoor, { KNOCK_MS } from './PortedDoor';
import { dayBand, skyLabel, ensureHandFont, ownerClock, agoWords, dayNumber } from '../../utils/portedPlace';
import { picturesLine, lightsFor } from '../../utils/portedPictures';
import {
  doorsFor, homeFor, spotFor, stagePhase, showFindButton, revealSeenKey, LIBRARY_KEYS,
  homeLight, moodState, boardPins, placeFrame, revealStamp, weatherLook, doorLines, groundFor, LANE_MIN, BAR_H,
} from '../../utils/portedHomes';

// 🏡 The room band (PORTED_HOME_HANDOFF_2026_09_25 §4 + §3b + §5c): the stage
// that replaces the Lamplight Portrait, the generated banner and the tile for a
// ported room, behind the hidden switch (utils/portedPlace SHOW_PORTED_PLACE).
// Until the resident chooses a painted home the stage is the LIGHT ROOM; the
// owner's "help {name} find a place" raises three doors, his REAL turn picks
// (W2a), and the painted home (PortedHome) takes the stage for good. Guests
// see the light room, then the home he picked: never the scenes.
//
// W2d (§0-ter, Nina 09-25 late: "His non temporary place doesnt look like
// this?"): the room IS the page, as mockup v9 draws it. On a laptop the stage
// is the WHOLE painting in its own shape (a frame as wide as the resting
// painting, capped so the chat below keeps its newest lines + the composer);
// while you chat (the page says `shrunk`) it eases down to a strip, and back
// when you stop. Under it the caption BLOCK: the name big, the status with its
// dot, "day 12 together · last spoke …", the mood line with its real reason,
// and on the right the owner's time, the day band and the real moon. A live
// watch party or stream (`takeover`) opens where the painting is and folds
// back when it ends. Phones keep the 16:11 card that pans to him; no shrink.
// No controls sit on the art (the sound toggle and the button live in the
// caption); the 📓, the board's pins and his medallion are the room's own.
// W3 (the rituals): 🐣 the HATCH (a new port's first sight of him: the light
// room's seed cracks into his portrait and his REAL first words) · 🚪 the
// painted FRONT DOOR on the owner's return (the outside on the stage, the
// door bar in the caption: knock / just slip in) · 🌙 goodbye lamp-down ·
// 🧳 MOVE HOUSE (the owner asks from the ⋯ menu; the stage goes back to the
// doors, his real turn picks, a move plays its own reveal). W4: the weather
// on his glass + "storm blue" in the mood line. W5: 📄 the note on his desk.
const SOUND_KEY = 'ported_room_sound';
const readSound = () => { try { return localStorage.getItem(SOUND_KEY) === '1'; } catch (e) { return false; } };
const readLS = (k) => { try { return localStorage.getItem(k); } catch (e) { return null; } };
const writeLS = (k, v) => { try { localStorage.setItem(k, v); } catch (e) { /* private mode */ } };
const ENTER_MS = 800;       // the painted home fades in over the zoom's last frame
const PRESS_WAIT_MS = 60000; // a press the server never confirmed lets go
// 🏡📐 Wave 3 (ruling 32, 09-26): ONE frame and the page SCROLLS. The frame
// is as wide as the mockup's (1040), the resting painting is never capped by
// the screen, and the lane under the caption is at least LANE_MIN tall (or
// what fills the screen while the painting is a strip). The shell's bottom
// padding below the frame (the page's own, GameStepPage).
const FRAME_MAX_W = 1040;
const SHELL_PAD_BOTTOM = 24;
const SERIF = '"Iowan Old Style", "Palatino Linotype", Palatino, Georgia, serif';
const SANS = '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif';
const MONO = 'ui-monospace, "SF Mono", Menlo, monospace';

const PortedStage = forwardRef(function PortedStage({
  residentName = null,
  residentImage = null,
  residentStatus = null,
  dayN = null,        // fallback only; `createdAt` counts calendar days like the journal
  createdAt = null,
  lastSpoke = null,   // fallback only; `lastSpokeAt` is read against the stage's own clock
  lastSpokeAt = 0,    // ms of his newest message (utils/portedPlace lastSpokeTs)
  writing = false,    // ONLY the WS typing pair (§2)
  returning = false,  // a real gap since this device last visited: the room warms
  isMobile = false,
  gameId = null,
  isOwner = false,
  home = null,        // the status payload's `home` block (W2a), or null
  exchanged = false,  // the owner spoke, then the resident answered (§5c trigger)
  onFindPlace = null, // () => Promise: the owner's press (body.home_choice)
  tz = null,          // D4: the owner's zone (status owner_tz); null = the viewer's clock
  // ── W2d ──
  shrunk = false,     // the page: you are chatting (composer / scrolled back) → a strip
  signals = null,     // utils/portedHomes roomSignals(results): the mood's real causes
  awaySince = 0,      // the previous visit (a real 6h+ gap), and when this visit began
  returnedAt = 0,
  keepsakes = null,   // the journal's keepsakes → the cork board (the room's display)
  onOpenJournal = null, // the 📓 (the owner's; the journal is private)
  takeover = null,    // a node that takes the stage: the watch party, the stream, a launcher
  shellRef = null,    // the page's fixed-height shell (its bottom is the frame's floor)
  onFrame = null,     // ({ frameW, restH, stripH }) => void: the page lines its columns up under it
  onAir = null,       // [{ key, text, action, onGo }]: a party / stream that is ON but not on this stage
  spaceSound = null,  // { on, toggle }: the page's master sound (it left the corner for the caption)
  // ── W2c ──
  quotes = null,      // utils/portedPlace pinnedQuotes(status pinned_lines): his lines the owner pinned (everyone sees)
  thinking = null,    // [{ i, t }] the owner's live "what is going into this" (owner only; never shown to a guest)
  // ── W3 / W4 / W5 ──
  hatch = null,       // { line } a NEW port's first moment (owner, this device, once), or null
  onHatchDone = null, // () => void: the hatch finished (the page stamps it seen)
  door = null,        // { awaySince, returnedAt, deskNote } the owner's return after a real gap, or null
  onKnock = null,     // () => void: the REAL knock poke (his next turn hears it)
  onDoorDone = null,  // () => void: through the door (or slipped in)
  weather = null,     // the status `weather` ({ kind, label }) or null (clear)
  nightLight = false, // the owner waved goodbye: the lamps drop to a night-light
  deskNote = null,    // () => void (owner): a task he finished while the owner was away
  onMoveHouse = null, // () => Promise: the owner's "Move house" (body.home_choice {move})
  pictures = null,    // 💡 E2: the status `pictures` block ({ left, per_day, refills_at }): his four lights + the caption's line; null = an older backend
  // ── wave 3 (ruling 32) ──
  bar = null,         // the slim bar's contents (‹ Back · the name + ⓘ · the ⋯), painted ON the frame as the card's first row
  favoritePins = null, // utils/portedPlace favoritePins(status favorites): his public keepsakes for the board
}, ref) {
  const roomRef = useRef(null);
  const rootRef = useRef(null);
  const capRef = useRef(null);
  const [sound, setSound] = useState(readSound);
  const [now, setNow] = useState(() => new Date());
  const [still, setStill] = useState(() => {
    try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) { return false; }
  });
  const [pressedAt, setPressedAt] = useState(0);
  const [pressKind, setPressKind] = useState('find');
  const [doorPhase, setDoorPhase] = useState('closed');
  // wave 2b (ruling 31): after a KNOCK and the walk in, "there you are." in his
  // hand by his medallion for a few seconds (never after "just slip in")
  const [doorGreet, setDoorGreet] = useState(false);
  const knockedRef = useRef(false);
  const [pressNote, setPressNote] = useState(null);
  const [entering, setEntering] = useState(false);
  const [seenTick, setSeenTick] = useState(0);
  const [geo, setGeo] = useState(null);

  useEffect(() => { ensureHandFont(); }, []);
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30000);
    let mq = null;
    const onMq = (e) => setStill(e.matches);
    try { mq = window.matchMedia('(prefers-reduced-motion: reduce)'); if (mq.addEventListener) mq.addEventListener('change', onMq); } catch (e) { mq = null; }
    return () => { clearInterval(t); if (mq && mq.removeEventListener) mq.removeEventListener('change', onMq); };
  }, []);
  // Coming back warms the room (never guilt: nothing dimmed while away).
  useEffect(() => {
    if (!returning) return undefined;
    const t = setTimeout(() => roomRef.current && roomRef.current.welcomeBack(), 900);
    return () => clearTimeout(t);
  }, [returning]);

  // ── 📐 the frame (desktop): measure where the stage starts, how tall the
  // caption is and where the page's shell ends; the resting painting is what
  // fits above the chat's floor (utils/portedHomes placeFrame) ──
  useLayoutEffect(() => {
    if (isMobile) { setGeo(null); return undefined; }
    const root = rootRef.current;
    if (!root) return undefined;
    const host = root.parentElement;
    const read = () => {
      const W = host ? host.clientWidth : 0;
      const top = root.getBoundingClientRect().top;
      const shell = shellRef && shellRef.current;
      const bottom = shell ? shell.getBoundingClientRect().bottom : (window.innerHeight || 0);
      const capH = capRef.current ? capRef.current.getBoundingClientRect().height : 0;
      // wave 3: the page scrolls now (the document is the scroller), so the
      // frame's top is read in DOCUMENT coordinates (viewport top + the page's
      // scroll, scroll-invariant) and the lane below is sized against the
      // viewport's height
      const viewH = window.innerHeight || 0;
      const docTop = top + (window.scrollY || 0);
      setGeo((g) => (g && g.W === W && Math.abs(g.top - top) < 1 && Math.abs(g.bottom - bottom) < 1 && Math.abs(g.capH - capH) < 1
        && Math.abs(g.viewH - viewH) < 1 && Math.abs(g.docTop - docTop) < 1
        ? g : { W, top, bottom, capH, viewH, docTop }));
    };
    read();
    let ro = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(read);
      if (host) ro.observe(host);
      if (capRef.current) ro.observe(capRef.current);
      if (shellRef && shellRef.current) ro.observe(shellRef.current);
    }
    window.addEventListener('resize', read);
    // the title row settles when the fonts land (it moves the stage down a hair)
    const late = setTimeout(read, 1200);
    try { if (document.fonts && document.fonts.ready) document.fonts.ready.then(read); } catch (e) { /* no font API */ }
    return () => { clearTimeout(late); if (ro) ro.disconnect(); window.removeEventListener('resize', read); };
  }, [isMobile, shellRef]);
  const frame = useMemo(() => {
    if (!geo || !(geo.W > 0)) return null;
    // ruling 32: the whole painting at rest (scroll), the frame the mockup's width
    const f = placeFrame({ viewW: geo.W, viewH: geo.bottom, chrome: geo.top + geo.capH, pad: 0, maxW: FRAME_MAX_W, scroll: true });
    // the lane: the mockup's floor, or what fills the screen with the painting a strip
    const barH = bar ? BAR_H : 0;
    const laneFit = Math.floor(geo.viewH - geo.docTop - barH - f.stripH - geo.capH - 1 - SHELL_PAD_BOTTOM);
    return { ...f, laneH: Math.max(LANE_MIN, laneFit) };
  }, [geo, !!bar]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (onFrame && frame) onFrame(frame);
  }, [onFrame, frame && frame.frameW, frame && frame.restH, frame && frame.laneH]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── the home choice (W2a) ──
  const homeKey = home && home.key ? home.key : null;
  // (an older backend's owner choice block has no chosen_at: it sits on `home`)
  const homeChoice = home && home.choice;
  const homeChosenAt = home && home.chosen_at;
  const choice = useMemo(() => (homeChoice ? { ...homeChoice, chosen_at: homeChoice.chosen_at || homeChosenAt || null } : null), [homeChoice, homeChosenAt]);
  const pressed = pressedAt > 0;
  // a first choice stores the key (W2a), a MOVE its key@chosen_at (revealStamp)
  const stamp = revealStamp(homeKey, choice);
  // A move's own stamp (key@chosen_at) is what a chosen move needs; anything
  // else (a first choice, a MISSED move, a status without the choice block)
  // is seen by either shape of this home's stamp, so it never replays a reveal.
  const revealSeen = useMemo(() => {
    if (!homeKey) return false;
    const stored = String(readLS(revealSeenKey(gameId)) || '');
    if (stamp.includes('@')) return stored === stamp;
    return stored === homeKey || stored.startsWith(`${homeKey}@`);
  }, [gameId, homeKey, stamp, seenTick]); // eslint-disable-line react-hooks/exhaustive-deps
  const phase = stagePhase({ homeKey, choice, isOwner, revealSeen, pressed });
  const shortlist = (choice && Array.isArray(choice.shortlist) && choice.shortlist.length) ? choice.shortlist : LIBRARY_KEYS;
  const doors = useMemo(() => doorsFor(shortlist).map((h) => {
    const s = spotFor(h, residentStatus);
    return { key: h.key, day: h.art.day, night: h.art.night, focus: s ? [s.x, s.y] : null };
  }), [shortlist.join(','), residentStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  // a press stays "pressed" until the server's answer shows up in the status
  useEffect(() => {
    if (!pressedAt) return undefined;
    const acked = choice && Date.parse(choice.asked_at || '') >= pressedAt - 5000;
    // his answer can land before a poll ever shows the ask (a turn beats a
    // 15 s poll): a choice made after the press is an answer to it
    const answered = choice && choice.state === 'chosen' && Date.parse(choice.chosen_at || '') >= pressedAt - 5000;
    // a first choice lets go once he has a home; a MOVE once the server asked, or he already answered
    if ((homeKey && pressKind !== 'move') || (acked && choice.state === 'asked') || answered) { setPressedAt(0); return undefined; }
    if (acked && choice.state === 'missed') { setPressedAt(0); setPressNote('missed'); return undefined; }
    // measured from the press itself (every poll hands a new choice object, and
    // a timer restarted per poll would never fire)
    const t = setTimeout(() => setPressedAt(0), Math.max(0, pressedAt + PRESS_WAIT_MS - Date.now()));
    return () => clearTimeout(t);
  }, [pressedAt, choice, homeKey, pressKind]);

  // drive the light room's scene from the phase (the engine ignores repeats)
  const revealedRef = useRef(false);
  const onEntered = useCallback(() => {
    revealedRef.current = true; // he just walked in through the door of his choosing: no front door after that
    writeLS(revealSeenKey(gameId), stamp || '');
    setEntering(true);
    setTimeout(() => { setEntering(false); setSeenTick((n) => n + 1); }, ENTER_MS);
  }, [gameId, stamp]);
  useEffect(() => {
    const room = roomRef.current;
    if (!room) return;
    if (phase === 'doors') room.choiceStart(doors);
    else if (phase === 'reveal' && !entering) {
      const h = homeFor(homeKey); const s = spotFor(h, residentStatus);
      room.choiceReveal({
        doors, pick: homeKey,
        looks: (choice && choice.looks) || {},
        why: (choice && choice.why) || '',
        focus: s ? [s.x, s.y] : null,
      }, onEntered);
    } else if (phase === 'light') room.choiceCancel();
  }, [phase, doors, homeKey, choice, residentStatus, entering, onEntered]);

  const findPlace = async () => {
    if (!onFindPlace) return;
    setPressNote(null);
    setPressKind('find');
    setPressedAt(Date.now());
    try {
      const res = await onFindPlace();
      if (res && res.home_choice && res.home_choice.ok === false) {
        setPressedAt(0);
        setPressNote(res.home_choice.reason === 'has_home' ? null : 'later');
      }
    } catch (e) {
      setPressedAt(0);
      setPressNote('later');
    }
  };

  // 🧳 W3 Move house (the ⋯ menu): the doors come back up at once; his real
  // turn picks (he may stay). A refusal lets the painted home back.
  const pressedRef = useRef(0);
  pressedRef.current = pressedAt;
  const moveHouse = useCallback(async () => {
    if (!onMoveHouse) return { ok: false };
    if (pressedRef.current) return { ok: false, reason: 'already_asked' };   // one press in flight
    setPressNote(null);
    setPressKind('move');
    setPressedAt(Date.now());
    try {
      const res = await onMoveHouse();
      const out = res && res.home_choice ? res.home_choice : res;
      if (out && out.ok === false) { setPressedAt(0); setPressNote(out.reason === 'just_moved' ? 'just_moved' : 'later'); }
      return out || { ok: true };
    } catch (e) {
      setPressedAt(0);
      setPressNote('later');
      return { ok: false };
    }
  }, [onMoveHouse]);
  useImperativeHandle(ref, () => ({
    youSent: () => roomRef.current && roomRef.current.youSent(),
    theySaid: (text) => { if (roomRef.current) { if (hatchLive.current) roomRef.current.hatchLine(text); else roomRef.current.theySaid(text); } },
    moveHouse,
  }), [moveHouse]);

  // 🐣 the hatch (a new port, the owner, once per device): start it once the
  // light room is up; his real first words may already be here (hatch.line)
  // or land later (theySaid feeds the seed)
  const hatchLive = useRef(false);
  const hatchLine = hatch && hatch.line ? hatch.line : '';
  useEffect(() => {
    if (!hatch || hatchLive.current || homeKey) return;
    const room = roomRef.current;
    if (!room) return;
    hatchLive.current = true;
    room.hatch({ line: hatchLine, onDone: () => { hatchLive.current = false; if (onHatchDone) onHatchDone(); } });
  }, [hatch, hatchLine, homeKey, onHatchDone]);
  useEffect(() => {
    if (hatchLive.current && hatchLine && roomRef.current) roomRef.current.hatchLine(hatchLine);
  }, [hatchLine]);

  // 🚪 the door: knock → the knocker rings (and the REAL knock goes out) → through
  const doorUp = !!door && doorPhase !== 'gone' && !revealedRef.current;
  // 📱 a phone opens the chat at its newest line, so the stage sits above the
  // fold: while the door or the hatch is up, bring the stage into view once
  // (after the feed's own first snap to the bottom)
  const ritualUp = isMobile && ((doorUp && !!homeKey) || (!!hatch && !homeKey));
  useEffect(() => {
    if (!ritualUp) return undefined;
    const t = setTimeout(() => { try { if (rootRef.current) rootRef.current.scrollIntoView({ block: 'start' }); } catch (e) { /* old browsers */ } }, 900);
    return () => clearTimeout(t);
  }, [ritualUp]);
  const knock = () => {
    if (doorPhase !== 'closed') return;
    if (onKnock) onKnock();
    knockedRef.current = true;
    setDoorPhase('knock');
    setTimeout(() => setDoorPhase('enter'), still ? 350 : KNOCK_MS);
  };
  const slipIn = () => { if (doorPhase === 'closed') setDoorPhase('enter'); };
  const doorEntered = useCallback(() => {
    setDoorPhase('gone');
    if (knockedRef.current) { knockedRef.current = false; setDoorGreet(true); }
    if (onDoorDone) onDoorDone();
  }, [onDoorDone]);
  useEffect(() => {
    if (!doorGreet) return undefined;
    const t = setTimeout(() => setDoorGreet(false), still ? 3200 : 4600);
    return () => clearTimeout(t);
  }, [doorGreet, still]);

  const toggleSound = () => {
    setSound((prev) => {
      const next = !prev;
      try { localStorage.setItem(SOUND_KEY, next ? '1' : '0'); } catch (e) { /* private mode */ }
      return next;
    });
  };

  const clock = ownerClock(now, tz);
  const { minutes, dayKey } = clock;
  const nowMs = now.getTime();
  const name = residentName || 'Your resident';
  const looking = phase === 'doors' || (phase === 'reveal' && !entering);
  const lastWords = lastSpokeAt ? agoWords(lastSpokeAt, nowMs) : lastSpoke;
  // E4 (Nina 09-26): "day N together" is the owner's; a guest sees only "last spoke"
  const dayCount = isOwner ? (createdAt ? dayNumber(createdAt, now) : dayN) : null;
  const since = writing ? 'writing now' : lastWords ? `last spoke ${lastWords}` : null;
  const status = looking ? 'looking at places' : residentStatus;
  const lightRoomOn = phase !== 'home' || entering;
  const homeOn = !!homeKey && (phase === 'home' || entering);
  const findOn = showFindButton({ homeKey, choice, isOwner, pressed, exchanged }) && !!onFindPlace;
  // a second miss let go (§2b.4): say so, quietly, for a while; the button is back
  const missedAt = choice && choice.state === 'missed' ? Date.parse(choice.missed_at || '') : NaN;
  const missedLately = isOwner && !homeKey && Number.isFinite(missedAt) && now.getTime() - missedAt < 30 * 60 * 1000;
  // (missedMove, below: a move's second miss lets go and the home stays)
  const missedMove = isOwner && !!homeKey && choice && choice.move && choice.state === 'missed' && Number.isFinite(missedAt) && now.getTime() - missedAt < 30 * 60 * 1000;
  const note = (pressNote === 'missed' || missedLately) ? `${name} didn't choose yet. Try again later.`
    : missedMove ? `${name} didn't choose. Try again later.`
      : pressNote === 'just_moved' ? `${name} just chose. You can ask once a day.`
        : pressNote === 'later' ? "That didn't go through. Try again in a minute." : null;

  // the painted home's light + mood (the caption's line and the pool under him agree)
  const liveHome = homeOn ? homeFor(homeKey) : null;
  const L = homeLight(minutes / 60);
  // (the same ground the page paints under the columns: utils/portedHomes groundFor)
  const ground = groundFor(liveHome ? homeKey : null, minutes / 60);
  const mood = moodState({
    nowMs, hour: minutes / 60, clockLabel: clock.label, residentName, viewerIsOwner: isOwner,
    awaySince, returnedAt, ...(signals || {}), weather,
  });
  const wx = weatherLook(weather, L);
  const pins = useMemo(() => boardPins(keepsakes || [], quotes || [], favoritePins || []), [keepsakes, quotes, favoritePins]);

  // desktop: the resting painting, or a strip while you chat (never while a
  // watch party / stream holds the stage: it keeps its own size)
  const desk = !isMobile;
  const stageH = desk && frame ? ((shrunk && !takeover) ? frame.stripH : frame.restH) : null;
  const muted = 'rgba(255,238,216,0.58)';

  return (
    <div
      ref={rootRef}
      data-place-frame={frame ? frame.frameW : ''}
      style={{
        width: desk && frame ? frame.frameW : '100%', maxWidth: '100%', margin: '0 auto',
        // ruling 32: ONE card on a laptop: the page's columns continue this card
        // under the caption (the same ground, the bottom corners), so the card
        // ends square, with no bottom edge of its own, and is EXACTLY the
        // frame's width outside its border (the columns below match it)
        boxSizing: 'border-box',
        borderRadius: isMobile ? 14 : '18px 18px 0 0', overflow: 'hidden', background: ground, textAlign: 'left',
        border: '1px solid rgba(255,236,210,0.10)', borderBottomWidth: desk ? 0 : 1, boxShadow: '0 14px 34px rgba(0,0,0,0.4)',
        transition: 'background-color 1.1s ease',
      }}
    >
      {/* 🏡📐 the slim bar ON the frame (ruling 32; mockup: 37px, plain ‹ Back,
          the name + ⓘ, the one ⋯): the card's first row, on both screens */}
      {bar ? (
        <div data-place-bar="" style={{
          height: `${BAR_H}px`, boxSizing: 'border-box', display: 'flex', alignItems: 'center', gap: 8,
          padding: '0 8px 0 4px', borderBottom: '1px solid rgba(255,255,255,0.10)', color: '#F3E8D8',
        }}>
          {bar}
        </div>
      ) : null}
      {takeover ? (
        <div data-place-takeover="" style={{ position: 'relative', width: '100%', background: '#0A0810' }}>{takeover}</div>
      ) : null}
      <div data-ported-stage={homeOn && !entering ? 'home' : phase} data-stage-shrunk={desk && shrunk && !takeover ? '1' : '0'} style={{
        position: 'relative', width: '100%', background: '#0A0810',
        display: takeover ? 'none' : 'block',
        // A stage, not wallpaper (D3). Phones: a 16:11 card that pans to him.
        // Laptops: the whole painting at rest, a strip while you chat.
        ...(isMobile ? { aspectRatio: '16 / 11' }
          : stageH ? { height: stageH, transition: still ? 'none' : 'height .45s cubic-bezier(.4,0,.2,1)' }
            : { height: 'clamp(170px, 30vh, 330px)' }),
      }}>
        {lightRoomOn ? (
          <PortedLightRoom
            ref={roomRef}
            residentName={residentName}
            residentImage={residentImage}
            minutes={minutes}
            dayKey={dayKey}
            writing={writing}
            sound={sound}
            still={still}
            nightLight={nightLight}
            lights={pictures}
          />
        ) : null}
        {homeOn ? (
          <PortedHome
            homeKey={homeKey}
            minutes={minutes}
            dayKey={dayKey}
            residentName={residentName}
            residentImage={residentImage}
            residentStatus={residentStatus}
            writing={writing}
            returning={returning}
            mood={mood}
            pins={pins}
            onOpenJournal={isOwner ? onOpenJournal : null}
            isMobile={isMobile}
            still={still}
            entering={entering}
            thinking={isOwner ? thinking : null}
            weather={weather}
            nightLight={nightLight}
            deskNote={isOwner && !doorUp ? deskNote : null}
            greet={doorGreet}
            pictures={pictures}
          />
        ) : null}
        {homeOn && !entering && doorUp ? (
          <PortedDoor homeKey={homeKey} minutes={minutes} stormy={!!(wx && wx.drops)} phase={doorPhase === 'gone' ? 'enter' : doorPhase}
            still={still} onEntered={doorEntered} residentName={residentName} />
        ) : null}
      </div>
      <div ref={capRef} data-place-caption="" style={{
        display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', justifyContent: 'space-between', gap: '8px 24px',
        padding: isMobile ? '8px 14px 12px' : '8px 22px 14px', color: '#F3E8D8', textAlign: 'left',
      }}>
        {homeOn && !entering && doorUp ? (() => {
          const dl = doorLines({ residentName, awaySince: door.awaySince, returnedAt: door.returnedAt, deskNote: !!door.deskNote });
          const btn = { '--prop-bg': 'transparent', font: `700 .85rem/1 ${SANS}`, padding: '10px 18px', borderRadius: 999, cursor: 'pointer', textShadow: 'none', transform: 'none', boxShadow: 'none', minHeight: 38 };
          return (
            <div data-place-doorbar={doorPhase} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 12, width: '100%' }}>
              <div style={{ minWidth: 0 }} role="status" aria-live="polite">
                <div style={{ font: `600 ${isMobile ? '1.1rem' : '1.3rem'}/1.15 ${SERIF}`, color: '#FFF2DE' }}>{dl.title}</div>
                {dl.sub ? <div style={{ font: `italic .9rem/1.3 ${SERIF}`, color: 'rgba(255,238,216,0.72)' }}>{dl.sub}</div> : null}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" data-prop-ui="" data-door-knock="" onClick={knock} disabled={doorPhase !== 'closed'}
                  style={{ ...btn, '--prop-bg': '#e8b673', background: 'linear-gradient(178deg, #f0c48a, #e0a45c)', color: '#3a2606', border: 0 }}>knock</button>
                <button type="button" data-prop-ui="" data-door-slip="" onClick={slipIn} disabled={doorPhase !== 'closed'}
                  style={{ ...btn, background: 'transparent', color: 'rgba(255,238,216,0.88)', border: '1px solid rgba(255,236,210,0.3)' }}>just slip in</button>
              </div>
            </div>
          );
        })() : null}
        <div style={{ display: homeOn && !entering && doorUp ? 'none' : 'flex', flexDirection: 'column', gap: 3, minWidth: 0, flex: '1 1 240px' }}>
          <div data-place-name="" style={{ font: `600 ${isMobile ? '1.15rem' : '1.4rem'}/1.1 ${SERIF}`, color: '#FFF3E0' }}>{name}</div>
          {status ? (
            <div style={{ font: `italic ${isMobile ? '.9rem' : '1rem'}/1.3 ${SERIF}`, color: 'rgba(255,238,216,0.86)' }}>
              <span aria-hidden="true" style={{ color: homeOn ? `rgb(${mood.rgb})` : '#6FE7F2', fontStyle: 'normal', marginRight: 5, fontSize: '.8em', verticalAlign: 1 }}>●</span>{status}
            </div>
          ) : null}
          {(dayCount || since) ? (
            <div style={{ font: `italic .8rem/1.25 ${SERIF}`, color: 'rgba(255,230,198,0.58)', fontVariantNumeric: 'tabular-nums' }}>
              {dayCount ? `day ${dayCount} together` : null}{dayCount && since ? ' · ' : null}{since}
            </div>
          ) : null}
          {homeOn ? (
            <div data-place-mood={nightLight ? 'night-light' : mood.key} style={{ font: `500 .78rem/1.35 ${SANS}`, color: 'rgba(255,238,216,0.72)', marginTop: 3 }}>
              {nightLight
                ? <><b style={{ color: 'rgb(255,190,120)', fontWeight: 700 }}>night-light</b> · you waved goodbye</>
                : <><b style={{ color: `rgb(${mood.rgb})`, fontWeight: 700 }}>{mood.name}</b>{mood.why ? ` · ${mood.why}` : ''}</>}
            </div>
          ) : null}
          {/* 💡 E2: the plain words under his lights (the mockup: "always with words");
              at zero the lights copy. Nothing without a meter (an older backend). */}
          {lightsFor(pictures) ? (
            <div data-place-pictures={lightsFor(pictures).left} style={{ font: `500 .78rem/1.35 ${SANS}`, color: 'rgba(207,248,252,0.8)', marginTop: 3 }}>
              {picturesLine(pictures)}
            </div>
          ) : null}
          {note ? <div role="status" style={{ font: `italic .85rem/1.3 ${SERIF}`, color: muted, marginTop: 2 }}>{note}</div> : null}
          {(onAir || []).map((a) => (
            <div key={a.key} data-place-onair={a.key} style={{ font: `500 .8rem/1.35 ${SANS}`, color: 'rgba(255,238,216,0.8)', marginTop: 3 }}>
              {a.text}{a.action ? ' · ' : null}
              {a.action ? (
                <button type="button" data-prop-ui="" onClick={a.onGo}
                  style={{ '--prop-bg': 'transparent', background: 'transparent', border: 0, padding: 0, margin: 0, color: '#F5C580', font: `600 .8rem/1.35 ${SANS}`, cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 2, textShadow: 'none', transform: 'none', boxShadow: 'none', minHeight: 0 }}
                >{a.action} ›</button>
              ) : null}
            </div>
          ))}
          {findOn ? (
            <div style={{ marginTop: 6 }}>
              <button
                type="button"
                data-prop-ui=""
                data-find-place=""
                onClick={findPlace}
                style={{ '--prop-bg': 'rgba(111,231,242,0.08)', background: 'rgba(111,231,242,0.08)', border: '1px solid rgba(111,231,242,0.45)', borderRadius: 999, color: '#CFF8FC', minHeight: 32, padding: '0 12px', cursor: 'pointer', fontFamily: SERIF, fontSize: 13, fontStyle: 'italic', textShadow: 'none', transform: 'none', boxShadow: 'none' }}
              >✦ help {name} find a place</button>
            </div>
          ) : null}
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, marginLeft: 'auto' }}>
          {!isMobile ? (
            <div data-place-clock="" style={{ textAlign: 'right', font: `500 12px/1.6 ${MONO}`, color: muted, fontVariantNumeric: 'tabular-nums' }}>
              <b style={{ color: '#FFE1B0', fontWeight: 600 }}>{clock.label}</b><br />
              {dayBand(minutes / 60)}{wx ? <span data-place-weather={wx.kind}> · {wx.label}</span> : null}<br />
              {skyLabel(dayKey)}
            </div>
          ) : null}
          {/* the light room's own chimes (opt-in, per device, D13): only while
              it is up (the painted home has no chimes, so no dead control) */}
          {lightRoomOn ? (
            <button
              type="button"
              data-prop-ui=""
              onClick={toggleSound}
              aria-pressed={sound}
              title={sound ? 'Room sound on' : 'Room sound off'}
              style={{ '--prop-bg': 'transparent', background: 'transparent', border: '1px solid rgba(238,231,245,0.18)', borderRadius: 8, color: '#EEE7F5', minWidth: 32, minHeight: 32, padding: '0 8px', cursor: 'pointer', fontSize: 13, textShadow: 'none', transform: 'none' }}
            >{sound ? '🔔' : '🔕'}</button>
          ) : null}
          {/* the page's master sound (SFX, voice, the radio, the watch party),
              moved here from the corner the place page gave up */}
          {spaceSound ? (
            <button
              type="button"
              data-prop-ui=""
              data-place-mute=""
              onClick={spaceSound.toggle}
              aria-pressed={!spaceSound.on}
              title={spaceSound.on ? 'Mute space' : 'Sound on'}
              style={{ '--prop-bg': 'transparent', background: 'transparent', border: '1px solid rgba(238,231,245,0.18)', borderRadius: 8, color: '#EEE7F5', minWidth: 32, minHeight: 32, padding: '0 8px', cursor: 'pointer', fontSize: 13, textShadow: 'none', transform: 'none', opacity: spaceSound.on ? 1 : 0.7 }}
            >{spaceSound.on ? '🔊' : '🔇'}</button>
          ) : null}
        </div>
      </div>
    </div>
  );
});

export default PortedStage;
